<script setup lang="ts">
import type { VNode } from "vue";
import type { RouteLocationRaw } from "vue-router";
import type { ParticipationResource } from "@carnet/api/backoffice";

const props = defineProps<{
  participation: ParticipationResource;
  to?: RouteLocationRaw;
}>();

const slots = defineSlots<{
  status?: () => VNode[];
  action?: () => VNode[];
  person?: () => VNode[];
  details?: () => VNode[];
}>();

const { t } = useI18n();

const activity = computed(() => props.participation.activity);

const typeIcon = computed(() => activityTypeIcon(activity.value?.type.code));

const formattedDate = computed(() => {
  if (!activity.value.endedAt) return "";
  return `${formatActivityDate(activity.value.endedAt)} \u2022 ${t("activity.endTime.short", { time: formatActivityTime(activity.value.endedAt) })}`;
});

const siteName = computed(() => getSiteDisplayName(activity.value, t));

const modeAndNumber = computed(() => {
  const mode = t(`activity.mode.${activity.value.mode.code}`);
  return props.participation.participationCounter > 0 ? `${mode} \u2022 N° ${props.participation.participationCounter}` : mode;
});

const creatorName = computed(() => {
  const creator = activity.value.creatorMember;
  if (!creator) return "";
  const member = activity.value.participations?.find((p) => p.id === props.participation.id)?.member;
  if (member && member.id === creator.id) return "";
  return memberFullName(creator);
});

// La validation n'existe que pour les sorties encadrées : ailleurs, ni badge de statut
// ni libellé d'accès qui en parle.
const { isSupervised, isSupervisor } = useAppActivityPermissions(activity);

const accessKey = computed(() => {
  if (!isSupervised.value || isSupervisor.value) return "activity.tile.access";
  switch (props.participation.validationStatus) {
    case "none":
      return "activity.tile.accessUnvalidated";
    case "refused":
      return "activity.tile.accessRefused";
    default:
      return "activity.tile.access";
  }
});
const accessLabel = computed(() => t(accessKey.value));
const accessAriaLabel = computed(() => t(`${accessKey.value}Aria`, { site: siteName.value, date: formattedDate.value }));
</script>

<template>
  <article class="group/tile @container/tile relative bg-white rounded-lg">
    <div class="px-4 @md/tile:px-6 @3xl/tile:px-10 py-6">
      <div class="grid @xl/tile:grid-cols-[2fr_1fr] gap-6">
        <div class="min-w-0 flex-1 grid grid-cols-[auto_1fr] gap-x-6 gap-y-4">
          <header class="col-span-2 grid grid-cols-subgrid gap-x-6 gap-y-4 @xl/tile:gap-y-3 items-start">
            <span class="shrink-0 bg-blue-800 text-white rounded-full size-10 flex items-center justify-center" aria-hidden="true">
              <UIcon :name="typeIcon" class="size-6" />
            </span>
            <div class="min-w-0">
              <p class="text-sm/5 text-gray-500">{{ modeAndNumber }}</p>
              <h3 class="text-xl/[1.3] @xl/tile:text-2xl/[1.33] font-semibold">{{ siteName }}</h3>
            </div>
            <div class="@max-xl/tile:col-span-2 @xl/tile:col-2">
              <UBadge color="primary" variant="soft">{{ formattedDate }}</UBadge>
            </div>
          </header>

          <div
            v-if="$slots.details || $slots.person || creatorName || activity.duration || activity.maxDepth"
            class="@max-xl/tile:col-span-2 @xl/tile:col-2 flex flex-col text-base"
          >
            <div v-if="$slots.person || creatorName || activity.duration || activity.maxDepth" class="flex flex-col gap-1">
              <slot name="person">
                <p v-if="creatorName">{{ t("activity.createdBy", { name: creatorName }) }}</p>
              </slot>
              <div v-if="activity.duration || activity.maxDepth" class="flex flex-wrap flex-col @xl/tile:flex-row @xl/tile:items-center gap-x-4">
                <span v-if="activity.duration">{{ t("activity.duration.label") }} : {{ formatSeconds(activity.duration) }}</span>
                <span v-if="activity.maxDepth">{{ t("activity.maxDepth.label") }} : {{ t("activity.depth", { depth: formatDecimal(activity.maxDepth) }) }}</span>
              </div>
            </div>
            <slot name="details" />
          </div>
        </div>

        <div class="shrink-0 flex flex-col @xl/tile:items-end justify-between self-stretch">
          <div class="grow w-full flex flex-col gap-2 @xl/tile:items-end">
            <slot name="status">
              <AppTileActivityStatus v-if="!slots.action" :participation class="mb-6" />
            </slot>
          </div>

          <slot name="action">
            <div v-if="to" class="flex items-center gap-4 justify-between">
              <span aria-hidden="true" class="font-semibold text-base">{{ accessLabel }}</span>
              <AppTileLink :to :aria-label="accessAriaLabel ?? accessLabel" icon="icon:arrowRight" />
            </div>
          </slot>
        </div>
      </div>
    </div>
  </article>
</template>
